import React from "react";
//#region types
import { DialogEnums } from "models/enums";
import { PeriodisationAdvancedDialogProps } from "models/propTypes/incomingInvoicePropTypes";
import { PeriodisationRowsFormValue } from "models/formTypes/incomingInvoice";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { Grid } from "@mui/material";
import Icon from "../icon";
import StaticInfoMessage from "../messages/staticInfoMessage";
import SimpleTooltip from "../tooltips/tooltip";
import DialogLayout from "../dialogs/_layout";
import FileViwerContextProvider from "../fileViewer/context/contextProvider";
import DimensionsDialogFilePortal from "../dimensions/dimensionsDialogFilePortal";
import PeriodisationRowsEditor from "./periodisationRowsEditor";
import { periodisationRowsSchema } from "./periodisationRowsSchema";
//#endregion
import { LocalizationHelperInstance, PeriodisationRowsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

// The tag dialog's shell (DimensionSelectDialog via TabContentDialog) without its tab bar: same layout, file portal,
// footer and save rule, with the periodisation rows as its only content.
const PeriodisationAdvancedDialog: React.FC<PeriodisationAdvancedDialogProps> = (props) => {
  const {
    isOpen,
    closeDialog,
    value: initialValue,
    currency,
    fileViewer,
    onSave,
    successMessage,
    invoiceMonth,
    isCredit,
  } = props;
  // Core validates the rows against the absolute net, so a credit invoice is split with positive amounts.
  const netAmount = Math.abs(props.netAmount);
  const [value, setValue] = React.useState<PeriodisationRowsFormValue>(() =>
    PeriodisationRowsHelper.toDialogValue(initialValue, netAmount)
  );
  // The portal target exists only once the dialog has mounted, so the file portal renders again on enter.
  const [filePortalKey, setFilePortalKey] = React.useState(0);

  const onEnter = () => {
    setValue(PeriodisationRowsHelper.toDialogValue(initialValue, netAmount));
    setFilePortalKey((key) => key + 1);
  };

  const isDirty = React.useMemo(
    () => JSON.stringify(value) !== JSON.stringify(PeriodisationRowsHelper.toDialogValue(initialValue, netAmount)),
    [value, initialValue, netAmount]
  );

  const remaining = PeriodisationRowsHelper.remaining(value, netAmount);
  // A single row with its period cleared saves as no periodisation, which is how an existing one is removed.
  const isCleared = value.rows.length === 1 && !value.rows[0].range?.start && !value.rows[0].range?.end;
  // Clearing only saves something when there was a periodisation to remove.
  const canSaveCleared = isCleared && PeriodisationRowsHelper.hasPeriodisation(initialValue);
  const isValid = React.useMemo(
    () => canSaveCleared || periodisationRowsSchema(netAmount).isValidSync(value),
    [canSaveCleared, netAmount, value]
  );
  const isComplete =
    remaining === 0 && value.rows.every((r) => PeriodisationRowsHelper.isRowComplete(r) && (r.amount ?? 0) > 0);
  // Removing an existing periodisation is a valid save, so the footer shows it as such instead of a warning.
  const isSaveable = isComplete || canSaveCleared;
  // Core saves such a split as no periodisation, so the user is told before saving rather than finding it gone.
  const isAllInInvoiceMonth =
    value.rows.length > 1 && PeriodisationRowsHelper.isSplitAllInInvoiceMonth(value, invoiceMonth);
  // Rows are positive amounts against the absolute net; a credit invoice shows its total negative like its rows, while
  // what is left keeps its sign so over-allocation still reads as negative.
  const formatAmount = (amount: number) => LocalizationHelperInstance.getFormattedCurrency(amount, currency, 2, 2);

  const save = async () => {
    await onSave(PeriodisationRowsHelper.fromDialogValue(value));
  };

  const statusText = LocalizationHelperInstance.getFormattedMessage(
    canSaveCleared
      ? TranslationKeys.Common.PeriodisationStatusCleared
      : isComplete
        ? TranslationKeys.Common.PeriodisationStatusComplete
        : remaining < 0
          ? TranslationKeys.Common.PeriodisationStatusOverAllocated
          : TranslationKeys.Common.PeriodisationStatusIncomplete
  );

  const getFooterIcon = () => {
    let iconClass = cssExports.success;
    let icon = IconDefinitionConstants.WCheck;

    if (!isSaveable) {
      iconClass = remaining < 0 ? cssExports.error : cssExports.warning;
      icon = IconDefinitionConstants.WExclamation;
    }

    return (
      <SimpleTooltip text={statusText}>
        <span>
          <Icon icon={icon} className={iconClass} />
        </span>
      </SimpleTooltip>
    );
  };

  return (
    <FileViwerContextProvider>
      <DialogLayout
        isOpen={isOpen}
        dialogType={DialogEnums.DialogType.Content}
        maxWidth="md"
        fullWidth
        title={LocalizationHelperInstance.getFormattedMessage(
          PeriodisationRowsHelper.hasPeriodisation(initialValue)
            ? TranslationKeys.IncomingInvoice.EditPeriodisation
            : TranslationKeys.IncomingInvoice.AddPeriodisation
        )}
        titleIcon={IconDefinitionConstants.WMonthCalendar}
        class={cssExports.dialogFullHeight}
        closeDialog={closeDialog}
        // With unsaved rows Esc and click outside are blocked, since there is no close confirmation yet.
        preventClickOutsideClose={isDirty}
        closeOnEscapeAnywhere
        onEnterHandler={onEnter}
        disableCancelWhileSubmitting
        actions={[
          {
            text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Save),
            onClick: save,
            disabled: !isValid,
            disabledTooltip: isSaveable ? undefined : statusText,
            successMessage,
          },
        ]}
        footer={
          <Grid item className={cssExports.dimensionSelectorFooter}>
            {canSaveCleared ? (
              // Nothing is left to periodise once the periodisation is removed, so the footer says what saving does.
              <label aria-live="polite">{statusText}</label>
            ) : (
              <>
                <label>
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.LeftToPeriodise, undefined, {
                    amount: formatAmount(isCredit ? -netAmount : netAmount),
                  })}
                  :
                </label>
                <label className={cssExports.dimensionSelectFooterValue} aria-live="polite">
                  {formatAmount(remaining)}
                </label>
              </>
            )}
            <label className={cssExports.dimensionSelectFooterIcon}>{getFooterIcon()}</label>
          </Grid>
        }
      >
        <React.Fragment>
          {fileViewer ? (
            <DimensionsDialogFilePortal key={filePortalKey}>{fileViewer}</DimensionsDialogFilePortal>
          ) : undefined}
          <div className={cssExports.editPeriodisationEditor}>
            {isAllInInvoiceMonth && (
              <StaticInfoMessage fullWidth gutterBottom>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PeriodisationAllInInvoiceMonth)}
              </StaticInfoMessage>
            )}
            <PeriodisationRowsEditor
              name="periodisation"
              value={value}
              setValue={setValue}
              netAmount={netAmount}
              currency={currency}
              isCredit={isCredit}
            />
          </div>
        </React.Fragment>
      </DialogLayout>
    </FileViwerContextProvider>
  );
};

export default PeriodisationAdvancedDialog;
